"use client";

import { motion } from "framer-motion";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@frontend/shared/components/ui/form";
import { zodResolver } from "@hookform/resolvers/zod";
import { ArrowLeft } from "lucide-react";
import { useTranslations } from "@frontend/lib/next-intl-typed";
import { useForm } from "react-hook-form";
import {
  useInsuranceConnectorContext,
  useInsuranceConnectorSend,
} from "../insurance-connector-provider";
import {
  type StateFormData,
  stateSchema,
} from "../machine/schemas/validation.schemas";
import { StateAutocomplete } from "./StateAutocomplete";

export function EligibilityStateQuestion() {
  const t = useTranslations("InsuranceConnector");
  const context = useInsuranceConnectorContext();
  const send = useInsuranceConnectorSend();

  const form = useForm<StateFormData>({
    resolver: zodResolver(stateSchema),
    defaultValues: {
      consumerState: context.consumerState || "",
    },
  });

  const onSubmit = (data: StateFormData) => {
    send({ type: "SUBMIT", data });
  };

  return (
    <div className="h-full flex flex-col justify-between pt-6 md:pt-[48px]">
      <Form {...form}>
        <form
          onSubmit={form.handleSubmit(onSubmit)}
          className="gap-4 flex flex-col h-full justify-between"
        >
          <div className="flex flex-col gap-4 md:gap-12">
            <h2 className="text-2xl md:text-[32px] leading-[120%] tracking-[-0.02em] font-semibold">
              {t("WhatStateLiveIn")}
            </h2>
            <FormField
              control={form.control}
              name="consumerState"
              render={({ field }) => (
                <FormItem>
                  <FormLabel className="sr-only">{t("State")}</FormLabel>
                  <FormControl>
                    <StateAutocomplete
                      value={field.value}
                      onChange={field.onChange}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>

          <div className="flex gap-4 justify-end md:justify-between items-center pt-3 md:pt-0">
            <motion.button
              type="button"
              onClick={() => send({ type: "BACK" })}
              className="hidden md:flex border border-black rounded-full aspect-square w-[62px] h-[62px] items-center justify-center"
              aria-label={t("GoBack")}
              initial={{ backgroundColor: "transparent", color: "#000000" }}
              whileHover={{ backgroundColor: "#000000", color: "#ffffff" }}
              transition={{ duration: 0.3, ease: "easeInOut" }}
            >
              <ArrowLeft className="h-6 w-6" />
            </motion.button>
            <motion.button
              type="submit"
              className="inline-flex items-center justify-center rounded-full px-[53px] md:px-[48px] py-5 md:py-[20px] text-base md:text-xl font-medium text-white"
              initial={{ backgroundColor: "#000000" }}
              whileHover={{ backgroundColor: "#00A3FF" }}
              transition={{ duration: 0.3, ease: "easeInOut" }}
            >
              {t("Next")}
            </motion.button>
          </div>
        </form>
      </Form>
    </div>
  );
}
